/*
  This file is contains types from https://github.com/SunRun/experience-payments-ui/blob/6b41371/src/types/external-message-types.ts
  It contains all of the types we use to communicate between experience-payments-UI and offer-experience
*/

type EventData = {
  title?: string;
  type: MessageType;
  payload: unknown;
};

enum MessageType {
  // Initialize autopay enrollment with given data
  INBOUND_INITIALIZE_AUTOPAY = "SUNRUN_PAYMENTS_UI_INBOUND_INITIALIZE_AUTOPAY",
  // Initialize the payment ui for a payment with given data
  INBOUND_INITIALIZE_PAYMENT = "SUNRUN_PAYMENTS_UI_INBOUND_INITIALIZE_PAYMENT",
  // Instructs the ui to go back to the previous step in the payment flow
  INBOUND_GO_BACK = "SUNRUN_PAYMENTS_UI_INBOUND_GO_BACK",
  // Signals that autopay enrollment was successful
  OUTBOUND_AUTOPAY_SUCCESS = "SUNRUN_PAYMENTS_UI_OUTBOUND_AUTOPAY_SUCCESS",
  // Signals that a payment was successful
  OUTBOUND_PAYMENT_SUCCESS = "SUNRUN_PAYMENTS_UI_OUTBOUND_PAYMENT_SUCCESS",
  // Signals that the customer has reached the review payment screen
  OUTBOUND_REVIEW_SCREEN_OPEN = "SUNRUN_PAYMENTS_UI_OUTBOUND_REVIEW_SCREEN_OPEN",
  // Analytics events that should be handled by the parent app
  OUTBOUND_ANALYTICS_EVENT = "SUNRUN_PAYMENTS_UI_OUTBOUND_ANALYTICS_EVENT",
  // Cybersource session has expired
  OUTBOUND_SESSION_EXPIRED = "SUNRUN_PAYMENTS_UI_OUTBOUND_SESSION_EXPIRED",
  // Signals that the payment ui has updated the title of the page
  OUTBOUND_UPDATE_MODAL_TITLE = "SUNRUN_PAYMENTS_UI_OUTBOUND_UPDATE_TITLE",
  // Signals that control should be returned to the parent app
  OUTBOUND_EXIT_PAYMENT_UI = "SUNRUN_PAYMENTS_UI_OUTBOUND_EXIT_PAYMENT_UI",
  // Log data that can be handled in the parent app as applicable
  OUTBOUND_LOG = "SUNRUN_PAYMENTS_UI_OUTBOUND_LOG",
  // Signals that the webview or iframe containing the payment ui has been resized
  OUTBOUND_WINDOW_RESIZE = "SUNRUN_PAYMENTS_UI_OUTBOUND_WINDOW_RESIZE",
  // @deprecated - Prefer OUTBOUND_EXIT_PAYMENT_UI; Pending deletion on https://sunrun.jira.com/browse/CA-4093
  OUTBOUND_CANCEL_PAYMENT = "SUNRUN_PAYMENTS_UI_OUTBOUND_CANCEL_PAYMENT",
  // @deprecated - Prefer OUTBOUND_EXIT_PAYMENT_UI; Pending deletion on https://sunrun.jira.com/browse/CA-4093
  OUTBOUND_INVALID_PHONE_NUMBER = "SUNRUN_PAYMENTS_UI_OUTBOUND_INVALID_PHONE_NUMBER",
  // @deprecated - Prefer OUTBOUND_EXIT_PAYMENT_UI; Pending deletion on https://sunrun.jira.com/browse/CA-4093
  OUTBOUND_SHOW_DISCARD_CHANGES_TOAST = "SUNRUN_PAYMENTS_UI_OUTBOUND_SHOW_DISCARD_CHANGES_TOAST",
  // Signals that the iframe/webview has mounted and is ready to receive messages from the parent app
  OUTBOUND_READY_TO_RECEIVE_MESSAGES = "SUNRUN_PAYMENTS_UI_OUTBOUND_READY_TO_RECEIVE_MESSAGES",
}

type Message =
  | {
      type: MessageType.INBOUND_INITIALIZE_AUTOPAY;
      payload: Inbound_InitializeAutopay_MessagePayload;
    }
  | {
      type: MessageType.INBOUND_INITIALIZE_PAYMENT;
      payload: Inbound_InitializePayment_MessagePayload;
    }
  | {
      type: MessageType.INBOUND_GO_BACK;
    }
  | {
      type: MessageType.OUTBOUND_AUTOPAY_SUCCESS;
      payload: Outbound_AutopaySuccess_MessagePayload;
    }
  | {
      type: MessageType.OUTBOUND_PAYMENT_SUCCESS;
      payload: Outbound_PaymentSuccess_MessagePayload;
    }
  | {
      type: MessageType.OUTBOUND_REVIEW_SCREEN_OPEN;
    }
  | {
      type: MessageType.OUTBOUND_ANALYTICS_EVENT;
      payload: OutboundPaymentAnalyticsEvent;
    }
  | {
      type: MessageType.OUTBOUND_SESSION_EXPIRED;
    }
  | {
      type: MessageType.OUTBOUND_UPDATE_MODAL_TITLE;
      payload: {
        title?: string;
      };
    }
  | {
      type: MessageType.OUTBOUND_EXIT_PAYMENT_UI;
      payload: Outbound_ExitPaymentUI_MessagePayload;
    }
  | {
      type: MessageType.OUTBOUND_LOG;
      payload: OutboundLog;
    }
  | {
      type: MessageType.OUTBOUND_WINDOW_RESIZE;
      payload: {
        height?: number;
      };
    }
  | {
      type: MessageType.OUTBOUND_CANCEL_PAYMENT;
    }
  | {
      type: MessageType.OUTBOUND_INVALID_PHONE_NUMBER;
    }
  | {
      type: MessageType.OUTBOUND_SHOW_DISCARD_CHANGES_TOAST;
    };

enum ErrorType {
  INVALID_PHONE = "invalid_phone",
}

interface UserInfo {
  address: string;
  address2?: string;
  city: string;
  contactId?: string;
  email?: string;
  firstName: string;
  lastName: string;
  mobilePhoneNumber?: string;
  phoneNumber?: string;
  proposalId?: string;
  prospectId: string;
  ptoDate?: string;
  serviceContractNumber?: string;
  state: string;
  zip: string;
  billingType?: string;
}

// method chosen by the user in the parent app
enum PayMethod {
  CARD = "card",
  CHECKING = "echeck",
  SAVINGS = "savings",
}

enum BillingScheduleTypeEnum {
  STANDARD_MONTHLY = "STANDARD_MONTHLY", // Monthly after pto date. Users with ptoDate on or after 2015-05-01 all use this.
  LEGACY_CALENDAR_MONTHLY = "LEGACY_CALENDAR_MONTH", // Beginning of each calendar month. Users with ptoDate before 2015-05-01 still use this.
}

enum SpecialInvoiceType {
  FIELD_SERVICE = "1_FS_C",
  CONTRACT_MOD = "1_SC_C",
  HOME_IMPROVEMENT = "1_HI_C",
  SYSTEM_PURCHASE = "1_SP_C",
  PREPAY = "UPFRONT",
}

// Base message payload type inherited by all initialization messages
interface InitializeBase_MessagePayload {
  authToken: string;
  payMethod: PayMethod;
  userInfo: UserInfo;
  showCancelPaymentButton?: boolean;
  scheduledAutoPayDate?: string;
  billingScheduleType?: BillingScheduleTypeEnum;
  repAssisted?: boolean; // process controlled by someone other than the customer
  isProposalToken?: boolean;
  locale?: string; // Language code (e.g., 'en', 'es') for internationalization
}

// INBOUND_INITIALIZE_AUTOPAY
interface Inbound_InitializeAutopay_MessagePayload
  extends InitializeBase_MessagePayload {
  isUpdate?: boolean;
}

// INBOUND_INITIALIZE_PAYMENT
interface Inbound_InitializePayment_MessagePayload
  extends InitializeBase_MessagePayload {
  lastDueDate?: string; // ISO date
  totalAmountDue?: number;
  amount: number;
  autopayEnrollmentEnabled: boolean; // Specify whether or not autopay is allowed for this user or scenario
  referenceNumberPrefix?: SpecialInvoiceType; // Optional: Used with certain one time payment types such as contract mod (1_SC_C_) or field service invoices (1_FC_C_)
  invoiceDate?: string;
  invoiceDueDate?: string;
  isDeposit?: boolean;
}

// OUTBOUND_AUTOPAY_SUCCESS
interface Outbound_AutopaySuccess_MessagePayload {
  lastFour?: string;
  // submissionDate: string; //ISO date
}

// OUTBOUND_PAYMENT_SUCCESS
interface Outbound_PaymentSuccess_MessagePayload {
  autopayEnrolled?: boolean;
  autopayEnrollmentFailed?: boolean;
  lastFour?: string;
  amount?: string;
  confirmationNumber?: string;
  email?: string;
  // submissionDate: string; //ISO date
}

interface Outbound_ExitPaymentUI_MessagePayload {
  hasUnsavedChanges?: boolean;
  error?: ErrorType;
}

interface OutboundPaymentAnalyticsEvent {
  trigger: string;
  event: Partial<{
    screenName: string;
    ctaName: string;
    billingAddress: "Pre-entered address" | "Use a different address";
    oneTimePaymentEnrollInAutoPay: boolean;
    userSelection: string;
    paymentMethod?: string;
  }>;
}

interface OutboundUpdateModalTitle {
  title: string;
}

interface OutboundLog {
  logLevel: LogLevel;
  message: string;
  params?: unknown;
}

type LogLevel = "info" | "warning" | "error" | "debug";

type ViewMode = "view" | "edit";

export {
  MessageType,
  ErrorType,
  PayMethod,
  BillingScheduleTypeEnum,
  SpecialInvoiceType,
};

export type {
  EventData,
  Message,
  UserInfo,
  InitializeBase_MessagePayload,
  Inbound_InitializeAutopay_MessagePayload,
  Inbound_InitializePayment_MessagePayload,
  Outbound_AutopaySuccess_MessagePayload,
  Outbound_PaymentSuccess_MessagePayload,
  Outbound_ExitPaymentUI_MessagePayload,
  OutboundPaymentAnalyticsEvent,
  OutboundUpdateModalTitle,
  OutboundLog,
  LogLevel,
  ViewMode,
};
